import React, { useEffect, useState } from 'react';
import { fetchUsers, deleteUserById } from '../../services/firestoreService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTrash } from '@fortawesome/free-solid-svg-icons';
import PageHead from '../../components/common/PageHead';

const ITEMS_PER_PAGE = 10;

const ViewUsers = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const getUsers = async () => {
      const fetchedUsers = await fetchUsers();
      setUsers(fetchedUsers);
    };
    getUsers();
  }, []);

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      await deleteUserById(userId);
      setUsers((prev) => prev.filter((u) => u.id !== userId));
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
  };

  const isWithinDateRange = (dateJoined) => {
    const date = dateJoined.toDate();
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;
    return (!start || date >= start) && (!end || date <= end);
  };

  const filteredUsers = users.filter(
    (u) =>
      (u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.username?.toLowerCase().includes(searchTerm.toLowerCase())) &&
      (roleFilter === '' || u.role === roleFilter) &&
      isWithinDateRange(u.dateJoined)
  );

  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const indexOfLast = currentPage * ITEMS_PER_PAGE;
  const indexOfFirst = indexOfLast - ITEMS_PER_PAGE;
  const currentUsers = filteredUsers.slice(indexOfFirst, indexOfLast);

  return (
    <>
      <PageHead name="All Users" />
      <div className="container mx-auto p-4 max-w-5xl">
        {/* Filters */}
        <div className="flex flex-col md:flex-row justify-between my-2 items-start md:items-center space-y-1 md:space-y-0 md:space-x-3">
          <input
            type="text"
            placeholder="Search by name or email"
            className="w-full px-4 py-2 border border-primary rounded-md shadow-lg focus:outline-none"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <FontAwesomeIcon icon={faSearch} className="text-xl text-primary hidden sm:block" />

          <select
            className="w-full md:w-48 px-4 py-2 border border-primary rounded-md"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="">All Roles</option>
            <option value="admin">Admin</option>
            <option value="user">User</option>
          </select>

          <input
            type="date"
            className="w-full md:w-48 px-4 py-2 border border-primary rounded-md"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <input
            type="date"
            className="w-full md:w-48 px-4 py-2 border border-primary rounded-md"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        {/* Users Table */}
        <table className="min-w-full bg-gray-100 table-auto text-left my-6 border border-gray-600">
          <thead className="bg-gray-200 text-gray-700 text-lg">
            <tr>
              <th className="border border-gray-600 px-4 py-2 w-1/12">#</th>
              <th className="border border-gray-600 px-4 py-2">Name</th>
              <th className="border border-gray-600 px-4 py-2">Email</th>
              <th className="border border-gray-600 px-4 py-2">Role</th>
              <th className="border border-gray-600 px-4 py-2">Date Joined</th>
              <th className="border border-gray-600 px-4 py-2 w-1/6">Actions</th>
            </tr>
          </thead>

          <tbody>
            {currentUsers.length > 0 ? (
              currentUsers.map((user, idx) => (
                <tr key={user.id} className="border-b border-gray-600 hover:bg-gray-100 transition">
                  <td className="h-20 px-4 border border-gray-600 text-center">{indexOfFirst + idx + 1}</td>
                  <td className="h-20 px-4 border border-gray-600">{user.username}</td>
                  <td className="h-20 px-4 border border-gray-600">{user.email}</td>
                  <td className="h-20 px-4 border border-gray-600">{user.role}</td>
                  <td className="h-20 px-4 border border-gray-600">{formatDate(user.dateJoined)}</td>
                  <td className="h-20 px-4 border border-gray-600">
                    <div className="flex justify-center items-center">
                      <button
                        onClick={() => handleDelete(user.id)}
                        className="p-2 rounded text-red-500 hover:bg-red-500 hover:text-white"
                        title="Delete User"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="text-center py-4">No users found</td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="flex justify-center mt-4">
          {Array.from({ length: totalPages }, (_, index) => (
            <button
              key={index + 1}
              onClick={() => setCurrentPage(index + 1)}
              className={`mx-1 py-1 px-3 font-bold rounded ${
                currentPage === index + 1 ? 'underline text-secondary' : 'text-primary'
              }`}
            >
              {index + 1}
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

export default ViewUsers;
